import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
} from '@angular/core';
import { v4 as uuid } from 'uuid';

export type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end' | number;
export type TXPosition = 'start' | 'center' | 'end' | number;

@Directive({
  selector: '[toggleElement]',
  standalone: true,
  exportAs: 'toggleElementDirective',
})
export class ToggleElementDirective implements OnInit, OnDestroy {
  private readonly el: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly refToggleElementId = uuid();

  /**
   * @param zIndexClass The class to apply the z-index.
   */
  public readonly zIndexClass: InputSignal<string> = input<string>();

  /**
   * @param anchor The anchor element to toggle the element.
   */
  public readonly anchor: InputSignal<HTMLElement> = input<HTMLElement>();

  /**
   * @param xAxis The x-axis position of the element.
   * @default 'start'
   */
  public xAxis: InputSignal<TXPosition> = input<TXPosition>('start');

  /**
   * @param yAxis The y-axis position of the element.
   * @default 'end'
   */
  public yAxis: InputSignal<TYPosition> = input<TYPosition>('end');

  /**
   * @param show If true, the element will be shown.
   */
  public readonly show: InputSignal<boolean> = input(false);

  /**
   * @param showChange The event emitter for the show signal.
   */
  public readonly showChange = output<boolean>();

  /**
   * @param toggleByAnchor If true, the element will be toggled by the anchor element.
   */
  public readonly toggleByAnchor = input<boolean>(true);

  /**
   * @param distance The distance between the element and the anchor.
   */
  public readonly distance: InputSignal<number> = input<number>(4);

  /**
   * @param closeInternalClick Remove the element when the user clicks inside the element.
   */
  public readonly closeInternalClick: InputSignal<boolean> = input<boolean>(true);

  /**
   * @param disabled If true, the directive will be disabled and no actions will be performed.
   * @default false
   */
  public readonly disabled: InputSignal<boolean> = input<boolean>(false);

  private wasDestroyed = false;

  private observeShow = effect(() => {
    if (this.disabled()) return;

    if (this.show()) this.addItem();
    else this.removeItem();
  });

  private observeToggleByAnchor = effect(() => {
    if (this.disabled()) return;

    const anchor = this.anchor();
    if (!anchor) return;
    const toggleByAnchor = this.toggleByAnchor();

    if (toggleByAnchor) {
      anchor.addEventListener('click', this.addItem, { passive: true });
      anchor.addEventListener('touchstart', this.addItem, { passive: true });
      return;
    }
    anchor.removeEventListener('click', this.addItem);
    anchor.removeEventListener('touchstart', this.addItem);
  });

  public ngOnInit(): void {
    const ref = document.createElement('div');
    ref.id = this.refToggleElementId;
    this.renderer.addClass(ref, 'hidden');
    this.el.nativeElement.replaceWith(ref);
    this.el.nativeElement.setAttribute('toggle-element-ref', this.refToggleElementId);
    this.renderer.addClass(this.el.nativeElement, 'hidden');
    this.renderer.addClass(this.el.nativeElement, this.zIndexClass() || 'z-[100]');
    this.renderer.addClass(this.el.nativeElement, 'absolute');
  }

  public ngOnDestroy(): void {
    this.wasDestroyed = true;
    this.removeItem();
  }

  public resize = (): void => {
    if (this.disabled()) return;  
    this.updatePosition();
  };

  public scroll = (event: WheelEvent | TouchEvent): void => {
    if (this.disabled()) return;  
    const target = event?.target as HTMLElement;
    if (
      target &&
      !this.el.nativeElement.contains(target) &&
      this.el.nativeElement.compareDocumentPosition(target) !== Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      requestAnimationFrame(() => this.updatePosition());
    }
  };

  public click = (event: MouseEvent | TouchEvent): void => {
    if (this.disabled()) return;  
    const target = event.target as HTMLElement;
    const toggleElementId = target.closest(`[toggle-element-ref]`)?.getAttribute('toggle-element-ref');
    const toggleElementRef = toggleElementId ? document.getElementById(toggleElementId) : null;
    const pageY = (event instanceof MouseEvent) ? event.pageY : (event.touches[0]?.pageY || 0);
    const pageX = (event instanceof MouseEvent) ? event.pageX : (event.touches[0]?.pageX || 0);

    const includeEl = (pageY >= this.el.nativeElement.offsetTop &&
      pageY <= this.el.nativeElement.offsetTop + this.el.nativeElement.offsetHeight &&
      pageX >= this.el.nativeElement.offsetLeft &&
      pageX <= this.el.nativeElement.offsetLeft + this.el.nativeElement.offsetWidth) || this.el.nativeElement.contains(target) || this.el.nativeElement.contains(toggleElementRef);

    const includeAnchor = this.anchor() && ((pageY >= this.anchor().offsetTop &&
      pageY <= this.anchor().offsetTop + this.anchor().offsetHeight &&
      pageX >= this.anchor().offsetLeft &&
      pageX <= this.anchor().offsetLeft + this.anchor().offsetWidth) || this.anchor().contains(target)) || this.anchor().contains(toggleElementRef);

    if (((this.closeInternalClick() && this.toggleByAnchor()) || !includeEl) && !includeAnchor) {
      this.removeItem();
    }
  };

  private updatePosition(): void {
    if (this.disabled() || !this.anchor()) return;  
    if (this.el.nativeElement.classList.contains('w-full'))
      this.renderer.setStyle(this.el.nativeElement, 'width', `${this.anchor().getBoundingClientRect().width}px`);

    const { width, height } = this.el.nativeElement.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    const xFreePosition = typeof this.xAxis() === 'number';
    const yFreePosition = typeof this.yAxis() === 'number';
    let left = xFreePosition ? this.xAxis() as number : this.calcLeftPosition(this.xAxis(), this.yAxis());
    let top = yFreePosition ? this.yAxis() as number : this.calcTopPosition(this.yAxis());

    if (top + height > innerHeight) top = yFreePosition ? top - height : this.calcTopPosition('start');
    if (top < 0) top = yFreePosition ? height : this.calcTopPosition('end');

    if (left + width > innerWidth) left = xFreePosition ? left - width : this.calcLeftPosition('end', this.yAxis());
    if (left < 0) left = xFreePosition ? width : this.calcLeftPosition('start', this.yAxis());

    if (left + width > innerWidth) left = 0;

    this.renderer.setStyle(this.el.nativeElement, 'top', `${top}px`);
    this.renderer.setStyle(this.el.nativeElement, 'left', `${left}px`);
  }

  private addItem = (): void => {
    if (this.disabled()) return;  

    if (document.body.contains(this.el.nativeElement)) return;
    document.addEventListener('wheel', this.scroll, { passive: true });
    document.addEventListener('touchmove', this.scroll, { passive: true });
    document.addEventListener('touchcancel', this.click, { passive: true });
    document.addEventListener('click', this.click, { passive: true });
    window.addEventListener('resize', this.resize, { passive: true });
    document.body.appendChild(this.el.nativeElement);
    this.showChange.emit(true);

    this.renderer.removeClass(this.el.nativeElement, 'hidden');
    this.updatePosition();
    this.el.nativeElement.animate(
      [
        { transform: 'scale(0.9)', opacity: 0 },
        { transform: 'scale(1)', opacity: 1 },
      ],
      {
        duration: 150,
        easing: 'ease',
      }
    );
  };

  private removeItem(): void {
    if (!document.body.contains(this.el.nativeElement)) return;
    document.removeEventListener('wheel', this.scroll);
    document.removeEventListener('touchmove', this.scroll);
    document.removeEventListener('touchcancel', this.click);
    document.removeEventListener('click', this.click);
    window.removeEventListener('resize', this.resize);
    if (document.body.contains(this.el.nativeElement)) document.body.removeChild(this.el.nativeElement);
    this.renderer.addClass(this.el.nativeElement, 'hidden');
    if (!this.wasDestroyed) this.showChange.emit(false);
  }

  private calcTopPosition(position: TYPosition): number {
    const { top, height } = this.anchor().getBoundingClientRect();
    const { height: elHeight } = this.el.nativeElement.getBoundingClientRect();
    const distance = this.distance();

    switch (position) {
      case 'start':
        return top - elHeight - distance;
      case 'center-start':
        return top;
      case 'center':
        return top + height / 2 - elHeight / 2;
      case 'center-end':
        return top + height - elHeight;
      case 'end':
        return top + height + distance;
      default:
        return top - elHeight - distance;
    }
  }

  private calcLeftPosition(positionX: TXPosition, positionY: TYPosition): number {
    const { left, width } = this.anchor().getBoundingClientRect();
    const { width: elWidth } = this.el.nativeElement.getBoundingClientRect();
    const distance = this.distance();

    const applyHorizontalDistance = positionY === 'center' || positionY === 'center-start' || positionY === 'center-end';

    switch (positionX) {
      case 'start':
        return applyHorizontalDistance ? left - elWidth - distance : left;
      case 'center':
        return left + width / 2 - elWidth / 2;
      case 'end':
        return applyHorizontalDistance ? left + width + distance : left + width - elWidth;
      default:
        return applyHorizontalDistance ? left - elWidth - distance : left;
    }
  }
}